import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  FileText, Loader2, Upload, Download, Crown, AlertCircle,
  Sparkles, History, Trash2, X, Printer, BookmarkPlus, Library,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

// Renders a memo's plain-text content into a printable HTML doc and
// triggers the browser's native print dialog. Native printing is the
// most reliable way to generate Arabic PDFs without a server-side
// font pipeline (matches the existing Contracts page approach).
//
// Implementation note: uses a Blob URL + hidden iframe instead of
// ``document.write`` — same end result, but avoids a pattern that
// generic XSS linters flag (the user-content is already HTML-escaped
// via ``safe()`` below, so this is defence in depth, not a bugfix).
function printMemo(memo, isAr) {
  const safe = (s) => (s || "").replace(/[&<>]/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;",
  }[c]));
  const headerEnglish = "ALDAKHEEL LEGAL GROUP";
  const headerArabic = "مجموعة الدخيل القانونية";
  const body = safe(memo.content || "")
    .split("\n")
    .map((line) => `<p>${line || "&nbsp;"}</p>`)
    .join("\n");
  const date = new Date(memo.created_at).toLocaleDateString(isAr ? "ar" : undefined);
  const html = `<!doctype html>
<html lang="${isAr ? "ar" : "en"}" dir="${isAr ? "rtl" : "ltr"}">
<head>
<meta charset="utf-8">
<title>${safe(memo.memo_label_ar || "Memo")}</title>
<style>
  @page { size: A4; margin: 22mm 18mm 22mm 18mm; }
  html, body { font-family: "Times New Roman", "Amiri", "Traditional Arabic", serif; font-size: 13pt; line-height: 1.85; color: #111; }
  body { padding: 0; margin: 0; }
  .head { border-bottom: 2px solid #111; margin-bottom: 18mm; padding-bottom: 6mm; display: flex; justify-content: space-between; align-items: baseline; }
  .head .ar { font-size: 18pt; font-weight: 700; }
  .head .en { font-size: 9pt; letter-spacing: 0.18em; text-transform: uppercase; }
  .meta { color: #555; font-size: 10pt; margin-bottom: 8mm; }
  p { margin: 0 0 4mm 0; text-align: justify; }
  .footer { margin-top: 12mm; padding-top: 4mm; border-top: 1px solid #ccc; font-size: 9pt; color: #777; }
</style>
</head>
<body>
  <div class="head">
    <div class="ar">${headerArabic}</div>
    <div class="en">${headerEnglish}</div>
  </div>
  <div class="meta">
    <strong>${safe(memo.memo_label_ar || "")}</strong>
    &middot; ${safe(memo.case_number || "")}
    &middot; ${date}
  </div>
  <div class="content">${body}</div>
  <div class="footer">
    ${isAr
      ? "هذه المذكرة مصاغة بمساعدة الذكاء الاصطناعي وتخضع لمراجعة محامٍ مرخّص قبل التقديم للمحكمة."
      : "This memorandum was drafted with AI assistance and is subject to review by a licensed attorney before court submission."}
  </div>
</body>
</html>`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "-10000px";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      // Clean up after the print dialog has had time to open.
      setTimeout(() => {
        URL.revokeObjectURL(url);
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 2000);
    }
  };
}

// ---------- Plans paywall ----------
function PaywallCard({ plans, current, onSubscribe, isAr, busy }) {
  return (
    <div className="grid md:grid-cols-3 gap-5" data-testid="memo-paywall">
      {plans.map((p) => {
        const isCurrent = p.id === current;
        const isFree = p.id === "free";
        return (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            className={`border bg-white p-6 flex flex-col ${
              isCurrent ? "border-slate-900 shadow-md" : "border-slate-200"
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              {p.id === "enterprise" ? <Crown className="w-4 h-4 text-amber-600" /> : null}
              <p className="eyebrow">{isAr ? p.name_ar : p.name_en}</p>
            </div>
            <p className="font-display text-3xl mt-2">
              {isFree ? (isAr ? "مجاناً" : "Free") : `${p.price_kwd} ${isAr ? "د.ك" : "KWD"}`}
              {!isFree ? <span className="text-sm text-slate-500">{isAr ? " / 30 يوم" : " / 30 days"}</span> : null}
            </p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700 flex-1">
              {(isAr ? p.features_ar : p.features_en).map((f, i) => (
                <li key={`feat-${p.id}-${i}-${(f || "").slice(0, 16)}`} className="flex items-start gap-2">
                  <span className="mt-1 inline-block w-1 h-1 rounded-full bg-slate-900" />
                  {f}
                </li>
              ))}
            </ul>
            {isFree ? (
              <p className="text-xs text-slate-500 mt-4">
                {isAr ? "خطتك الحالية" : "Your current plan"}
              </p>
            ) : isCurrent ? (
              <span className="mt-4 px-2 py-0.5 self-start text-xs uppercase tracking-[0.18em] border border-emerald-200 bg-emerald-50 text-emerald-700">
                {isAr ? "مُفعّل" : "Active"}
              </span>
            ) : (
              <Button
                type="button"
                onClick={() => onSubscribe(p.id)}
                disabled={busy}
                data-testid={`subscribe-${p.id}`}
                className="mt-4 rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isAr ? "اشترك الآن" : "Subscribe"}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------- Main memo composer ----------
export default function SamiMemoMode() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const isAr = lang === "ar";

  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [memoTypes, setMemoTypes] = useState([]);
  const [memos, setMemos] = useState([]);

  const [memoType, setMemoType] = useState("defense_memo");
  // Per-party fields (May 2026 — explicit instead of free-form).
  const [yourName, setYourName] = useState("");
  const [yourCapacity, setYourCapacity] = useState("");
  const [petitionerParty, setPetitionerParty] = useState("");
  const [yourCivilId, setYourCivilId] = useState("");
  const [yourAddress, setYourAddress] = useState("");
  const [opponentName, setOpponentName] = useState("");
  const [opponentCapacity, setOpponentCapacity] = useState("");
  const [opponentCivilId, setOpponentCivilId] = useState("");
  const [opponentAddress, setOpponentAddress] = useState("");
  const [opponentIdDoc, setOpponentIdDoc] = useState(null); // {id, filename, size_bytes}
  const [uploadingOpponentId, setUploadingOpponentId] = useState(false);
  const opponentIdInputRef = useRef(null);
  const [caseNumber, setCaseNumber] = useState("");
  const [court, setCourt] = useState("");
  const [reliefSought, setReliefSought] = useState("");
  const [caseFacts, setCaseFacts] = useState("");
  const [extra, setExtra] = useState("");
  const [docs, setDocs] = useState([]); // {id, filename, size_bytes}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null); // memo currently shown
  const [errMsg, setErrMsg] = useState("");
  const fileInputRef = useRef(null);

  // ---- Anonymised template library ----
  // Lawyers can save any generated memo as a re-usable template. All
  // names + IDs are stripped server-side before storage. Re-applying a
  // template means: load its skeleton text into the case_facts area so
  // it gets re-sent to Sami with the lawyer's new party data.
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const reload = useCallback(() => {
    Promise.all([
      api.get("/sami/subscription"),
      api.get("/sami/plans"),
      api.get("/sami/memo-types"),
      api.get("/sami/memos"),
    ]).then(([s, p, t, m]) => {
      setSub(s.data);
      setPlans(p.data?.plans || []);
      setMemoTypes(t.data?.types || []);
      setMemos(m.data || []);
    }).catch(() => {
      setSub({ tier: "free", plan: { id: "free", memos_per_month: 0 }, active: false });
    });
  }, []);

  useEffect(() => { if (user) reload(); }, [user, reload]);

  // Subscription redirect (after MyFatoorah callback)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("sub") === "success") {
      setErrMsg("");
      reload();
      const url = new URL(window.location.href);
      url.searchParams.delete("sub");
      window.history.replaceState({}, "", url.toString());
    }
  }, [reload]);

  // ---------- Templates: load / save / apply ----------
  // Defined here (above any early returns) to satisfy Rules of Hooks.
  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/sami/templates");
      setTemplates(data || []);
    } catch {
      setTemplates([]);
    }
  }, []);

  // Refresh templates when the user's subscription/role allows memo drafting.
  useEffect(() => {
    if (!sub || !user) return;
    const canDraft = user.role === "staff" || user.role === "admin"
      || (sub.plan?.memos_per_month || 0) > 0;
    if (canDraft) loadTemplates();
  }, [sub, user, loadTemplates]);

  const t = useMemo(() => isAr ? {
    title: "سامي PRO",
    lead: "اكتب مذكرات الدفاع، صحف الدعاوى، حوافظ المستندات، ومذكرات الطعن — بصياغة محامٍ كويتي خبير، مع الاستشهاد التلقائي بالمواد ومبادئ التمييز.",
    signInRequired: "هذه الميزة تتطلب تسجيل الدخول. يُرجى تسجيل الدخول أولاً.",
    chooseType: "نوع المذكرة",
    yourName: "اسمك",
    yourNamePh: "الاسم الكامل كما يظهر في المستندات الرسمية",
    yourCapacity: "صفتك",
    yourCapacityPh: "اختر صفتك من القائمة",
    yourCivilId: "الرقم المدني",
    yourCivilIdPh: "12 رقماً",
    yourCivilIdRequired: "(إلزامي لصحيفة الدعوى)",
    yourAddress: "عنوانك",
    yourAddressPh: "مثال: الكويت، حولي، شارع تونس، قطعة 4، منزل 12",
    opponentName: "اسم خصمك",
    opponentNamePh: "اسم الطرف الآخر الكامل",
    opponentCapacity: "صفة خصمك",
    opponentCapacityPh: "اختر صفة الخصم من القائمة",
    opponentCivilId: "الرقم المدني للخصم",
    opponentCivilIdPh: "12 رقماً (اختياري إن كان غير معروف)",
    opponentAddress: "عنوان الخصم",
    opponentAddressPh: "العنوان كاملاً (المنطقة، القطعة، الشارع، رقم المنزل)",
    opponentIdUpload: "رفع البطاقة المدنية واستخراج البيانات تلقائياً — اختياري",
    opponentIdUploadHint: "ميزة مساعدة تُسرّع تعبئة الحقول أعلاه. يمكنك تجاهلها تماماً وكتابة بيانات الخصم يدوياً — فلا يُشترط رفع البطاقة لإنشاء المستند.",
    opponentIdUploaded: (n) => `تم الرفع: ${n}`,
    opponentIdRemove: "إزالة",
    capacityOpts: [
      "مدّعي",
      "مدّعى عليه",
      "محامي المدّعي",
      "محامي المدّعى عليه",
      "طاعن",
      "مطعون ضده",
      "مستأنف",
      "مستأنف ضده",
      "متظلِّم",
      "متظلَّم ضده",
      "مُنذِر",
      "مُنذَر إليه",
      "طالب التحكيم",
      "المطلوب ضده التحكيم",
      "ممثل قانوني",
      "وكيل بموجب توكيل",
    ],
    caseNumber: "رقم الدعوى",
    caseNumberPh: "مثال: 1234 لسنة 2026 تجاري كلي",
    court: "المحكمة / الجهة",
    courtPh: "مثال: محكمة الكويت الكلية - الدائرة التجارية",
    relief: "الطلبات المرجوة",
    reliefPh: "مثال: رفض الدعوى وإلزام المدعية بالمصاريف",
    facts: "وقائع القضية",
    factsPh: "اشرح الوقائع بالتفصيل: ماذا حدث، متى، الأطراف، المستندات، النزاع. كلما زاد التفصيل، كانت المذكرة أدق.",
    extra: "تعليمات إضافية (اختياري)",
    extraPh: "مثلاً: ركّز على الدفع بالبطلان، أو أَدرج طلب وقف التنفيذ.",
    uploadDocs: "إرفاق مستندات",
    uploadHint: "PDF / صور / Word — حتى 25 م.ب لكل ملف. يستخرج سامي النص ويستفيد منه في الصياغة.",
    chooseFile: "اختر ملفاً",
    uploading: "جارٍ الرفع...",
    generate: "إنشاء المذكرة",
    generating: "جارٍ الصياغة (قد يستغرق 60 ثانية)...",
    quotaUsed: (used, max) => `استُخدم ${used} من ${max} مذكرة هذا الشهر`,
    expires: "تنتهي الباقة في",
    upgradeRequired: "تحتاج إلى ترقية باقتك للوصول إلى صياغة المذكرات.",
    plansTitle: "اختر الباقة المناسبة",
    plansLead: "ارفع كفاءتك القانونية مع سامي PRO — صياغة احترافية بمستوى المحامي.",
    history: "مذكراتي السابقة",
    noMemos: "لم تنشئ أي مذكرة بعد.",
    open: "فتح",
    delete: "حذف",
    deleteConfirm: "هل تريد حذف هذه المذكرة نهائياً؟",
    print: "طباعة / PDF",
    backToList: "← رجوع",
    saveAsTemplate: "حفظ كقالب (بدون أسماء)",
    savingTemplate: "جارٍ الحفظ…",
    useTemplate: "تحميل من قالب",
    myTemplates: "قوالبي المحفوظة",
    noTemplates: "لم تحفظ أي قوالب بعد. اضغط «حفظ كقالب» بعد توليد مذكرة لتعيد استخدام بنيتها لاحقاً.",
    templateLibraryHint: "تحفظ القوالب بدون أي بيانات شخصية للأطراف (الأسماء، الصفات، الأرقام المدنية، التواريخ، المبالغ، أرقام القضايا) — تُستبدل بمتغيّرات مثل [اسم الطرف الأول].",
    deleteTemplate: "حذف القالب",
    closeLibrary: "إغلاق",
    samiDisclaimer: "ملاحظة: هذه المذكرة مصاغة بمساعدة الذكاء الاصطناعي. يجب مراجعتها من محامٍ مرخّص قبل تقديمها للمحكمة.",
    activate: "تم تفعيل اشتراكك بنجاح! يمكنك الآن صياغة المذكرات.",
  } : {
    title: "Sami PRO",
    lead: "Draft defense memos, claim petitions, document indexes, and cassation briefs — at expert Kuwaiti-counsel quality, with automatic citations to articles and Cassation principles.",
    signInRequired: "This feature requires sign-in. Please log in first.",
    chooseType: "Memo type",
    yourName: "Your name",
    yourNamePh: "Full legal name as it appears on official documents",
    yourCapacity: "Your capacity",
    yourCapacityPh: "Select your capacity",
    yourCivilId: "Civil ID",
    yourCivilIdPh: "12 digits",
    yourCivilIdRequired: "(required for claim petition)",
    yourAddress: "Your address",
    yourAddressPh: "Block / street / building",
    opponentName: "Opponent's name",
    opponentNamePh: "Full name of the opposing party",
    opponentCapacity: "Opponent's capacity",
    opponentCapacityPh: "Select opponent's capacity",
    opponentCivilId: "Opponent's Civil ID",
    opponentCivilIdPh: "12 digits (optional if unknown)",
    opponentAddress: "Opponent's address",
    opponentAddressPh: "Full address (area, block, street, building)",
    opponentIdUpload: "Or upload the opponent's Civil ID image",
    opponentIdUploadHint: "PDF or image. Sami will extract the data.",
    opponentIdUploaded: (n) => `Uploaded: ${n}`,
    opponentIdRemove: "Remove",
    capacityOpts: [
      "Claimant",
      "Defendant",
      "Counsel for Claimant",
      "Counsel for Defendant",
      "Appellant",
      "Appellee",
      "Cassation Petitioner",
      "Cassation Respondent",
      "Notifier",
      "Notified party",
      "Arbitration Claimant",
      "Arbitration Respondent",
      "Legal Representative",
      "Power-of-Attorney Holder",
    ],
    caseNumber: "Case number",
    caseNumberPh: "e.g. 1234/2026 commercial",
    court: "Court / authority",
    courtPh: "e.g. Court of First Instance - Commercial",
    relief: "Relief sought",
    reliefPh: "e.g. dismiss the claim and award costs",
    facts: "Case facts",
    factsPh: "Describe the facts in detail: what happened, when, the parties, documents, the dispute. The more detail, the better the memo.",
    extra: "Additional instructions (optional)",
    extraPh: "e.g. focus on the nullity argument; include a stay-of-execution request.",
    uploadDocs: "Attach documents",
    uploadHint: "PDF / images / Word — up to 25 MB each. Sami extracts the text and uses it in the draft.",
    chooseFile: "Choose file",
    uploading: "Uploading…",
    generate: "Generate memo",
    generating: "Drafting (up to 60 seconds)…",
    quotaUsed: (used, max) => `${used} of ${max} memos used this period`,
    expires: "Plan expires on",
    upgradeRequired: "Upgrade your plan to unlock memo drafting.",
    plansTitle: "Choose your plan",
    plansLead: "Level up with Sami PRO — court-grade drafting on demand.",
    history: "My memos",
    noMemos: "You haven't drafted any memos yet.",
    open: "Open",
    delete: "Delete",
    deleteConfirm: "Permanently delete this memo?",
    print: "Print / PDF",
    backToList: "← Back",
    saveAsTemplate: "Save as template (no names)",
    savingTemplate: "Saving…",
    useTemplate: "Load template",
    myTemplates: "My saved templates",
    noTemplates: "No templates saved yet. Click \"Save as template\" after generating a memo to re-use its structure later.",
    templateLibraryHint: "Templates are saved with all party data stripped (names, capacities, civil IDs, dates, amounts, case numbers) — replaced by placeholders such as [اسم الطرف الأول].",
    deleteTemplate: "Delete template",
    closeLibrary: "Close",
    samiDisclaimer: "Note: this memo was drafted with AI assistance. Have a licensed attorney review it before court submission.",
    activate: "Subscription activated! You can now draft memos.",
  }, [isAr]);

  if (!user) {
    return (
      <div className="border border-dashed border-slate-300 p-16 text-center text-slate-500" data-testid="memo-signin-needed">
        <AlertCircle className="w-6 h-6 mx-auto text-slate-400 mb-3" />
        {t.signInRequired}
      </div>
    );
  }

  if (!sub) {
    return <div className="p-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>;
  }

  const onSubscribe = async (planId) => {
    setBusy(true);
    setErrMsg("");
    try {
      const { data } = await api.post("/sami/subscribe", { plan: planId });
      if (data.payment_url) window.location.href = data.payment_url;
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Could not start payment.");
      setBusy(false);
    }
  };

  // Platform is free for all logged-in users — memo drafting is always
  // available once signed in (the `!user` guard above handles sign-in).
  const canGenerate = true;

  // ---------- Doc upload ----------
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErrMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sami/memos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocs((prev) => [...prev, data]);
      e.target.value = "";
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // Separate uploader for the opponent's Civil ID image. Reuses the
  // same /sami/memos/upload endpoint (the backend extracts text from
  // the PDF/image OCR layer) but stores the resulting doc id on its
  // own state slot so it doesn't appear in the general "documents"
  // list — and so the memo prompt can reference it explicitly.
  const onOpponentIdUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingOpponentId(true);
    setErrMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sami/memos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOpponentIdDoc(data);
      e.target.value = "";
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingOpponentId(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!active) return;
    const suggested = `${active.memo_label_ar || ""} — ${
      (caseFacts || "").slice(0, 40)
    }`.trim() || (isAr ? "قالب جديد" : "New template");
    const title = window.prompt(
      isAr ? "اسم القالب (سيُحفظ بدون أسماء أطراف):" : "Template name (saved without party names):",
      suggested,
    );
    if (title === null) return; // cancelled
    setSavingTemplate(true);
    try {
      await api.post("/sami/templates", {
        memo_id: active.id,
        memo_type: active.memo_type,
        title,
        content: active.content,
        your_name: yourName || undefined,
        your_capacity: yourCapacity || undefined,
        opponent_name: opponentName || undefined,
        opponent_capacity: opponentCapacity || undefined,
        case_number: caseNumber || undefined,
      });
      await loadTemplates();
      toast.success(isAr ? "تم حفظ القالب بدون أسماء." : "Template saved without names.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isAr ? "تعذّر الحفظ" : "Save failed"));
    } finally {
      setSavingTemplate(false);
    }
  };

  const applyTemplate = async (tplId) => {
    try {
      const { data } = await api.get(`/sami/templates/${tplId}`);
      // The template ships with placeholders ([اسم الطرف الأول] …).
      // We pre-fill the case-facts area so the lawyer sees the skeleton
      // and can edit it before re-sending to Sami with the fresh names.
      setMemoType(data.memo_type || "defense_memo");
      setCaseFacts(data.content || "");
      setTemplatesOpen(false);
      setActive(null);
      toast.success(
        isAr
          ? "تم تحميل القالب — يمكنك الآن إضافة أسماء الأطراف وتوليد المذكرة."
          : "Template loaded — fill in party names and regenerate.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isAr ? "تعذّر تحميل القالب" : "Load failed"));
    }
  };

  const deleteTemplate = async (tplId) => {
    if (!window.confirm(isAr ? "حذف هذا القالب نهائياً؟" : "Delete this template permanently?")) return;
    try {
      await api.delete(`/sami/templates/${tplId}`);
      await loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ---------- Generate ----------
  const onGenerate = async () => {
    if (!caseFacts.trim()) {
      setErrMsg(isAr ? "يُرجى كتابة وقائع القضية أولاً." : "Please describe the case facts first.");
      return;
    }
    // Hard validation: Civil ID is mandatory for petition memos.
    const isPetition = memoType === "claim_petition" || memoType === "appeal_petition" || memoType === "appeal";
    if (memoType === "claim_petition" && !yourCivilId.trim()) {
      setErrMsg(
        isAr
          ? "الرقم المدني للمدّعي إلزامي في صحيفة الدعوى."
          : "The claimant's Civil ID is mandatory for a claim petition."
      );
      return;
    }
    if (isPetition && !petitionerParty) {
      setErrMsg(
        isAr
          ? "يُرجى تحديد مقدم الصحيفة: وكيل الطالب المحامي أو الطالب بشخصه."
          : "Please choose who is filing the petition: lawyer agent or self."
      );
      return;
    }
    setBusy(true);
    setErrMsg("");
    try {
      const { data } = await api.post("/sami/memos/generate", {
        memo_type: memoType,
        case_facts: caseFacts,
        your_name: yourName || undefined,
        your_capacity: yourCapacity || undefined,
        your_civil_id: yourCivilId || undefined,
        your_address: yourAddress || undefined,
        opponent_name: opponentName || undefined,
        opponent_capacity: opponentCapacity || undefined,
        opponent_civil_id: opponentCivilId || undefined,
        opponent_address: opponentAddress || undefined,
        opponent_civil_id_doc_id: opponentIdDoc?.id || undefined,
        petitioner_party: petitionerParty || undefined,
        case_number: caseNumber || undefined,
        court_or_authority: court || undefined,
        relief_sought: reliefSought || undefined,
        extra_instructions: extra || undefined,
        document_ids: docs.map((d) => d.id),
        language: isAr ? "ar" : "en",
      }, { timeout: 180000 });
      setActive({
        id: data.id,
        memo_type: memoType,
        memo_label_ar: data.memo_label_ar,
        case_number: caseNumber,
        content: data.content,
        created_at: new Date().toISOString(),
      });
      reload();
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onOpenMemo = async (id) => {
    try {
      const { data } = await api.get(`/sami/memos/${id}`);
      setActive({ ...data, memo_type: data.memo_type || memoType });
    } catch {
      setErrMsg(isAr ? "تعذّر فتح المذكرة." : "Could not open the memo.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.deleteConfirm)) return;
    try {
      await api.delete(`/sami/memos/${id}`);
      reload();
      if (active?.id === id) setActive(null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          (isAr ? "تعذّر حذف المذكرة." : "Could not delete memo."),
      );
    }
  };

  // ---------- Render ----------
  return (
    <div data-testid="sami-memo-mode">
      <div className="grid lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-7">
          <p className="eyebrow">{isAr ? "سامي PRO" : "Sami PRO"}</p>
          <h2 className="font-display text-3xl lg:text-4xl mt-2 leading-tight">{t.title}</h2>
          <p className="mt-3 text-slate-600">{t.lead}</p>
        </div>
        <div className="lg:col-span-5 lg:pt-3">
          <div className="border border-slate-200 p-4 bg-slate-50">
            <p className="eyebrow">{isAr ? "حالة الاشتراك" : "Subscription"}</p>
            <p className="font-display text-xl mt-1">
              {isAr ? sub.plan?.name_ar : sub.plan?.name_en}
            </p>
            {sub.expires_at ? (
              <p className="text-xs text-slate-500 mt-1">
                {t.expires}: {new Date(sub.expires_at).toLocaleDateString(isAr ? "ar" : undefined)}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Paywall for free users */}
      {!canGenerate ? (
        <div className="mb-8" data-testid="memo-paywall-section">
          <h3 className="font-display text-2xl mb-2">{t.plansTitle}</h3>
          <p className="text-slate-600 mb-6">{t.plansLead}</p>
          <PaywallCard
            plans={plans}
            current={sub.plan?.id}
            onSubscribe={onSubscribe}
            isAr={isAr}
            busy={busy}
          />
          {errMsg ? <p className="mt-4 text-sm text-rose-700">{errMsg}</p> : null}
        </div>
      ) : null}

      {/* Composer + memo viewer */}
      {canGenerate ? (
        active ? (
          // ---- Memo viewer ----
          <div className="grid lg:grid-cols-12 gap-6" data-testid="memo-viewer">
            <div className="lg:col-span-12">
              <button
                type="button"
                onClick={() => setActive(null)}
                data-testid="memo-back"
                className="text-xs uppercase tracking-[0.18em] text-slate-600 hover:text-slate-900 mb-4"
              >
                {t.backToList}
              </button>
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                  <p className="eyebrow">{active.memo_label_ar}</p>
                  <h3 className="font-display text-2xl mt-1">
                    {active.case_number || (isAr ? "بدون رقم دعوى" : "No case number")}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={saveAsTemplate}
                    disabled={savingTemplate}
                    data-testid="memo-save-template"
                    className="rounded-sm h-10 bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300"
                  >
                    {savingTemplate ? (
                      <Loader2 className="w-4 h-4 me-2 animate-spin" />
                    ) : (
                      <BookmarkPlus className="w-4 h-4 me-2" />
                    )}
                    {savingTemplate ? t.savingTemplate : t.saveAsTemplate}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => printMemo(active, isAr)}
                    data-testid="memo-print"
                    className="rounded-sm h-10 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <Printer className="w-4 h-4 me-2" /> {t.print}
                  </Button>
                </div>
              </div>
              <div
                data-testid="memo-content"
                dir="rtl"
                className="border border-slate-200 bg-white p-8 lg:p-12 whitespace-pre-line text-base leading-[1.95]"
                style={{ fontFamily: "Times New Roman, Amiri, serif" }}
              >
                {active.content}
              </div>
              <p className="mt-4 text-xs text-slate-500" dir="auto">{t.samiDisclaimer}</p>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8" data-testid="memo-composer">
            {/* Form */}
            <div className="lg:col-span-7 space-y-5">
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="eyebrow block mb-2">{t.chooseType}</label>
                  <select
                    data-testid="memo-type"
                    value={memoType}
                    onChange={(e) => setMemoType(e.target.value)}
                    className="w-full border border-slate-200 rounded-sm h-11 px-3 text-sm bg-white"
                  >
                    {memoTypes.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label_ar} ({m.for_party_ar})
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => setTemplatesOpen(true)}
                  data-testid="memo-open-template-library"
                  className="inline-flex items-center gap-2 h-11 px-4 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-sm transition-colors"
                >
                  <Library className="w-4 h-4" />
                  {t.useTemplate}
                  {templates.length > 0 && (
                    <span className="text-xs bg-amber-200 text-amber-900 rounded-sm px-1.5 py-0.5">
                      {templates.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Your side */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">{t.yourName}</label>
                  <Input
                    dir="auto"
                    data-testid="memo-your-name"
                    value={yourName}
                    onChange={(e) => setYourName(e.target.value)}
                    placeholder={t.yourNamePh}
                    className="rounded-sm h-11"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.yourCapacity}</label>
                  <select
                    data-testid="memo-your-capacity"
                    value={yourCapacity}
                    onChange={(e) => setYourCapacity(e.target.value)}
                    dir={isAr ? "rtl" : "ltr"}
                    className="flex h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20"
                  >
                    <option value="">{t.yourCapacityPh}</option>
                    {t.capacityOpts.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Civil ID + address for the user's side */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">
                    {t.yourCivilId}
                    {memoType === "claim_petition" && (
                      <span
                        className="ms-2 text-[10px] normal-case tracking-normal"
                        style={{ color: "var(--gold-deep)" }}
                      >
                        {t.yourCivilIdRequired}
                      </span>
                    )}
                  </label>
                  <Input
                    dir="ltr"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={12}
                    data-testid="memo-your-civil-id"
                    value={yourCivilId}
                    onChange={(e) => setYourCivilId(e.target.value.replace(/\D/g, ""))}
                    placeholder={t.yourCivilIdPh}
                    className="rounded-sm h-11"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.yourAddress}</label>
                  <Input
                    dir="auto"
                    data-testid="memo-your-address"
                    value={yourAddress}
                    onChange={(e) => setYourAddress(e.target.value)}
                    placeholder={t.yourAddressPh}
                    className="rounded-sm h-11"
                  />
                </div>
              </div>

              {/* Opponent side */}
              <div
                className="text-[11px] text-slate-500 leading-relaxed -mb-1"
                data-testid="opponent-entry-hint"
                dir={isAr ? "rtl" : "ltr"}
              >
                {isAr
                  ? "بيانات الخصم: اكتبها يدوياً في الحقول التالية، أو ارفع صورة بطاقته المدنية أدناه، أو استخدم الطريقتين معاً — البيانات تبقى دائماً قابلة للتعديل."
                  : "Opponent details: type them manually below, upload their Civil ID image, or do both — all extracted fields remain editable."}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">{t.opponentName}</label>
                  <Input
                    dir="auto"
                    data-testid="memo-opponent-name"
                    value={opponentName}
                    onChange={(e) => setOpponentName(e.target.value)}
                    placeholder={t.opponentNamePh}
                    className="rounded-sm h-11"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.opponentCapacity}</label>
                  <select
                    data-testid="memo-opponent-capacity"
                    value={opponentCapacity}
                    onChange={(e) => setOpponentCapacity(e.target.value)}
                    dir={isAr ? "rtl" : "ltr"}
                    className="flex h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20"
                  >
                    <option value="">{t.opponentCapacityPh}</option>
                    {t.capacityOpts.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Opponent Civil ID + address */}
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">{t.opponentCivilId}</label>
                  <Input
                    dir="ltr"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={12}
                    data-testid="memo-opponent-civil-id"
                    value={opponentCivilId}
                    onChange={(e) => setOpponentCivilId(e.target.value.replace(/\D/g, ""))}
                    placeholder={t.opponentCivilIdPh}
                    className="rounded-sm h-11"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">{t.opponentAddress}</label>
                  <Input
                    dir="auto"
                    data-testid="memo-opponent-address"
                    value={opponentAddress}
                    onChange={(e) => setOpponentAddress(e.target.value)}
                    placeholder={t.opponentAddressPh}
                    className="rounded-sm h-11"
                  />
                </div>
              </div>

              {/* Petitioner party selector — required for petition memos */}
              {(memoType === "claim_petition" ||
                memoType === "appeal_petition" ||
                memoType === "appeal") && (
                <div
                  className="border-s-4 ps-4 py-3 bg-amber-50/40"
                  style={{ borderColor: "var(--gold-deep)" }}
                  data-testid="petitioner-party-block"
                >
                  <label className="eyebrow block mb-2">
                    {isAr ? "مقدم الصحيفة" : "Filed by"}
                    <span
                      className="ms-2 text-[10px] normal-case tracking-normal"
                      style={{ color: "var(--gold-deep)" }}
                    >
                      {isAr ? "(إلزامي)" : "(required)"}
                    </span>
                  </label>
                  <select
                    data-testid="memo-petitioner-party"
                    value={petitionerParty}
                    onChange={(e) => setPetitionerParty(e.target.value)}
                    dir={isAr ? "rtl" : "ltr"}
                    className="flex h-11 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20"
                  >
                    <option value="">
                      {isAr
                        ? "— اختر مقدم الصحيفة —"
                        : "— choose filer —"}
                    </option>
                    <option value="lawyer_agent">
                      {isAr
                        ? "وكيل الطالب المحامي (المحامي عبداللطيف سامي الدخيل — قيد 7507)"
                        : "Lawyer agent (Abdullatif Sami Aldakheel — bar #7507)"}
                    </option>
                    <option value="self">
                      {isAr ? "الطالب بشخصه" : "Self-represented"}
                    </option>
                  </select>
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed">
                    {isAr
                      ? "ملاحظة: رقم الدائرة والرقم الآلي للقضية يُتركان فارغين تماماً — تحدّدهما المحكمة بعد التسجيل."
                      : "Note: court circuit and case auto-number stay blank — assigned by court after filing."}
                  </p>
                </div>
              )}

              {/* Opponent ID card upload (optional alternative to manual entry) */}
              <div
                className="border border-dashed border-slate-300 rounded-sm p-4 bg-slate-50/40"
                data-testid="memo-opponent-id-upload-block"
              >
                <label className="eyebrow block mb-1">{t.opponentIdUpload}</label>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                  {t.opponentIdUploadHint}
                </p>
                <input
                  ref={opponentIdInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={onOpponentIdUpload}
                  className="hidden"
                  data-testid="memo-opponent-id-file-input"
                />
                {opponentIdDoc ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="truncate" data-testid="memo-opponent-id-name">
                        ✓ {t.opponentIdUploaded(opponentIdDoc.filename)}{" "}
                        <span className="text-slate-400">
                          ({formatBytes(opponentIdDoc.size_bytes)})
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpponentIdDoc(null)}
                        data-testid="memo-opponent-id-remove"
                        className="rounded-sm h-8 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-3.5 h-3.5 me-1" />
                        {t.opponentIdRemove}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      {isAr
                        ? "ملاحظة: البيانات المكتوبة يدوياً في الحقول أعلاه تأخذ الأولوية. إذا اختلفت عمّا في البطاقة فسامي يعتمد ما كتبتَه."
                        : "Note: manually-typed fields above take priority. If they differ from the uploaded ID, Sami follows what you typed."}
                    </p>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => opponentIdInputRef.current?.click()}
                    disabled={uploadingOpponentId}
                    data-testid="memo-opponent-id-upload-btn"
                    className="rounded-sm h-10"
                  >
                    {uploadingOpponentId ? (
                      <Loader2 className="w-3.5 h-3.5 me-2 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5 me-2" />
                    )}
                    {isAr ? "اختر ملف البطاقة" : "Choose ID file"}
                  </Button>
                )}
              </div>

              <div>
                <label className="eyebrow block mb-2">{t.caseNumber}</label>
                <Input
                  dir="auto"
                  data-testid="memo-case-number"
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                  placeholder={t.caseNumberPh}
                  className="rounded-sm h-11"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2">{t.court}</label>
                <Input
                  dir="auto"
                  data-testid="memo-court"
                  value={court}
                  onChange={(e) => setCourt(e.target.value)}
                  placeholder={t.courtPh}
                  className="rounded-sm h-11"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2">{t.relief}</label>
                <Input
                  dir="auto"
                  data-testid="memo-relief"
                  value={reliefSought}
                  onChange={(e) => setReliefSought(e.target.value)}
                  placeholder={t.reliefPh}
                  className="rounded-sm h-11"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2">{t.facts} <span className="text-rose-700">*</span></label>
                <textarea
                  dir="auto"
                  data-testid="memo-facts"
                  value={caseFacts}
                  onChange={(e) => setCaseFacts(e.target.value)}
                  placeholder={t.factsPh}
                  rows={8}
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm leading-relaxed"
                />
              </div>

              <div>
                <label className="eyebrow block mb-2">{t.extra}</label>
                <textarea
                  dir="auto"
                  data-testid="memo-extra"
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  placeholder={t.extraPh}
                  rows={3}
                  className="w-full border border-slate-200 rounded-sm p-3 text-sm leading-relaxed"
                />
              </div>

              {/* Uploaded docs */}
              <div className="border border-slate-200 p-4 bg-slate-50" data-testid="memo-docs">
                <p className="eyebrow mb-2">{t.uploadDocs}</p>
                <p className="text-xs text-slate-600 mb-3">{t.uploadHint}</p>
                {docs.length > 0 ? (
                  <ul className="mb-3 space-y-1">
                    {docs.map((d) => (
                      <li key={d.id} className="flex items-center justify-between text-xs bg-white border border-slate-200 px-3 py-2">
                        <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-slate-400" /> {d.filename}</span>
                        <span className="text-slate-500">{formatBytes(d.size_bytes)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <label className={`inline-flex items-center gap-2 px-4 h-10 border border-slate-300 cursor-pointer text-xs uppercase tracking-[0.18em] hover:border-slate-900 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {uploading ? t.uploading : t.chooseFile}
                  <input
                    ref={fileInputRef}
                    type="file"
                    data-testid="memo-doc-input"
                    className="hidden"
                    onChange={onUpload}
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.txt"
                  />
                </label>
              </div>

              {errMsg ? (
                <div className="border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="memo-error">
                  {errMsg}
                </div>
              ) : null}

              <Button
                type="button"
                onClick={onGenerate}
                disabled={busy || !caseFacts.trim()}
                data-testid="memo-generate"
                className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white text-base"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin me-2" /> : <Sparkles className="w-5 h-5 me-2" />}
                {busy ? t.generating : t.generate}
              </Button>
            </div>

            {/* History pane */}
            <aside className="lg:col-span-5" data-testid="memo-history">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-slate-500" />
                <p className="eyebrow">{t.history}</p>
              </div>
              {memos.length === 0 ? (
                <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                  {t.noMemos}
                </div>
              ) : (
                <ul className="border border-slate-200 divide-y divide-slate-200 bg-white">
                  {memos.map((m) => (
                    <li key={m.id} data-testid={`memo-row-${m.id}`} className="p-4 hover:bg-slate-50">
                      <p className="font-medium text-sm">
                        {m.memo_label_ar} {m.case_number ? `· ${m.case_number}` : ""}
                      </p>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                        {(m.case_facts || "").slice(0, 80)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(m.created_at).toLocaleString(isAr ? "ar" : undefined)}
                      </p>
                      <div className="mt-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onOpenMemo(m.id)}
                          data-testid={`memo-open-${m.id}`}
                          className="text-xs uppercase tracking-[0.18em] border-b border-slate-900 pb-0.5"
                        >
                          {t.open}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(m.id)}
                          data-testid={`memo-delete-${m.id}`}
                          className="text-xs uppercase tracking-[0.18em] text-rose-700 hover:underline ms-auto"
                        >
                          {t.delete}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </aside>
          </div>
        )
      ) : null}

      {/* ---- Template library overlay ---- */}
      {templatesOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
          onClick={() => setTemplatesOpen(false)}
          data-testid="memo-template-library-overlay"
        >
          <div
            className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-slate-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="eyebrow">{t.myTemplates}</p>
                <h3 className="font-display text-xl mt-1">
                  {templates.length}{" "}
                  {isAr ? "قالب محفوظ" : "saved templates"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setTemplatesOpen(false)}
                data-testid="memo-template-library-close"
                className="p-2 hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 text-xs text-slate-500 border-b border-slate-100">
              {t.templateLibraryHint}
            </div>
            {templates.length === 0 ? (
              <p
                className="px-6 py-12 text-center text-slate-500 text-sm"
                data-testid="memo-template-library-empty"
              >
                {t.noTemplates}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {templates.map((tpl) => (
                  <li
                    key={tpl.id}
                    data-testid={`memo-template-${tpl.id}`}
                    className="px-6 py-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-[0.18em] text-amber-700">
                          {tpl.memo_label_ar}
                        </p>
                        <p
                          dir="auto"
                          className="font-medium text-slate-900 mt-1 truncate"
                        >
                          {tpl.title}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(tpl.created_at).toLocaleDateString(
                            isAr ? "ar-KW" : "en-GB",
                          )}{" "}
                          · {tpl.char_count?.toLocaleString()}{" "}
                          {isAr ? "حرف" : "chars"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => applyTemplate(tpl.id)}
                          data-testid={`memo-template-apply-${tpl.id}`}
                          className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-800"
                        >
                          {isAr ? "استخدام" : "Use"}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteTemplate(tpl.id)}
                          data-testid={`memo-template-delete-${tpl.id}`}
                          className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 text-rose-700 hover:bg-rose-50 border border-rose-200"
                        >
                          {t.deleteTemplate}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
